import { validators } from "../utils/validators";
import { inputValidTargetAttrName } from "../const/const";

import {
  get,
  useController,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { useEffect } from "react";

/**
 * Single Input Validation 체크 전용 훅
 * @param {string} name - (required) 필드 이름
 * @param {import("react-hook-form").ValidationRule} [rules] - (optional) 유효 검증 조건
 * @param {any} [defaultValue] - (optional) 필드 기본값
 * @returns {{
 *   inputProps: {
 *     value: any,
 *     onChange: Function,
 *     onBlur: Function,
 *     ref: React.Ref
 *   },
 *   error: import("react-hook-form").FieldError | undefined,
 *   onValueChange: (e: any) => Promise<void>
 * }}
 */
const useValidCheckSingle = ({ name, rules, defaultValue }) => {
  const { control, trigger, resetField } = useFormContext();
  const { errors } = useFormState({ name, control });

  const error = get(errors, name);
  const controller = useController({ name, control, rules, defaultValue });

  const inputProps = {
    value: controller.field.value,
    onChange: controller.field.onChange,
    onBlur: controller.field.onBlur,
    ref: controller.field.ref,
  };

  useEffect(() => {
    if (controller.field.value == null && defaultValue != null) {
      resetField(name, {
        defaultValue: defaultValue,
        keepError: false,
        keepDirty: false,
        keepTouched: false,
      });
    }
  }, [defaultValue, name, resetField]);

  const onValueChange = async (e) => {
    const value = e?.target?.value ?? e;
    console.log(value);
    controller.field.onChange(value);
    if (error) {
      await trigger(name);
    }
  };

  return {
    inputProps,
    error,
    onValueChange,
  };
};

/**
 * Group Input(Checkbox, Multi-select 등) Validation 체크 전용 훅
 * @param {string} name - (required) 필드 이름
 * @param {import("react-hook-form").ValidationRule} [rules] - (optional) 유효 검증 조건
 * @returns {{
 *   inputProps: {
 *     value: any[],
 *     onChange: Function,
 *     onBlur: Function,
 *     ref: React.Ref,
 *     name: string
 *   },
 *   error: import("react-hook-form").FieldError | undefined,
 *   onValueChange: (newValue: any[]) => void
 * }}
 */
const useValidCheckGroup = (name, rules) => {
  const { control, setValue, watch } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const currentValue = watch(name);

  useEffect(() => {
    if (field.value === undefined) {
      setValue(name, []);
    }
  }, []);

  const onValueChange = (newValue) => {
    field.onChange(newValue);
  };

  return {
    inputProps: { ...field, value: currentValue ?? field.value },
    error,
    onValueChange,
  };
};

///TODO: delete later
const isFormInvalid = (inputError) =>
  inputError?.hasError && !!inputError.error?.message;

const findInputError = (errors, name) => {
  const error = errors[name];
  return error ? { hasError: true, error } : { hasError: false };
};

const validateInputs = (opt) => {
  const targetScroll = opt?.targetScroll;
  const inputsToValidate = document.querySelectorAll(
    `[${inputValidTargetAttrName}]`,
  );

  for (const inputEl of inputsToValidate) {
    const validationType = inputEl.getAttribute("valid-type");
    const inputValue = inputEl.value;
    const fieldTitle =
      inputEl.getAttribute("valid-title") || inputEl.getAttribute("title");

    const validatorFn = validators[validationType];

    if (!validatorFn?.(inputValue)) {
      if (targetScroll) {
        inputEl.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return {
        isValid: false,
        invalidElement: inputEl,
        invalidTitle: fieldTitle,
      };
    }
  }

  return {
    isValid: true,
    invalidElement: null,
    invalidTitle: null,
  };
};

export {
  useValidCheckSingle,
  useValidCheckGroup,
  findInputError,
  isFormInvalid,
  validateInputs,
};
